import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import api from '../utils/api';
import { AlertTriangle, Upload, CheckCircle, Car } from 'lucide-react';
import toast from 'react-hot-toast';

const DAMAGE_TYPES = ['Unfall','Vandalismus','Hagel','Glasbruch','Motorschaden','Reifenschaden','Wasserschaden','sonstiges'];

export default function SchadenQR() {
  const { vehicleId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [images, setImages] = useState([]);
  const [form, setForm] = useState({
    title: '', description: '', damage_date: new Date().toISOString().split('T')[0],
    location: '', damage_type: 'sonstiges'
  });

  useEffect(() => {
    if (!user) { navigate(`/login?redirect=/schaden/qr/${vehicleId}`); return; }
    api.get(`/vehicles/${vehicleId}`).then(r => setVehicle(r.data))
      .catch(() => toast.error('Fahrzeug nicht gefunden'))
      .finally(() => setLoading(false));
  }, [vehicleId, user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const fd = new FormData();
      fd.append('vehicle_id', vehicleId);
      Object.entries(form).forEach(([k,v]) => { if (v) fd.append(k, v); });
      images.forEach(img => fd.append('images', img));
      await api.post('/damage', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Fehler beim Senden');
    } finally { setSaving(false); }
  };

  const f = (k,v) => setForm(p => ({ ...p, [k]: v }));

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)' }}>
      <div className="spinner" style={{ width: 32, height: 32, borderWidth: 3 }} />
    </div>
  );

  if (submitted) return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24, textAlign: 'center' }}>
      <div style={{ width: 72, height: 72, background: '#ecfdf5', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 20 }}>
        <CheckCircle size={36} color="#10b981" />
      </div>
      <h1 style={{ fontSize: '1.4rem', fontWeight: 800, marginBottom: 10 }}>Schadensmeldung gesendet!</h1>
      <p style={{ color: 'var(--text-muted)', marginBottom: 6 }}>
        Fahrzeug: <strong>{vehicle?.brand} {vehicle?.model}</strong>
      </p>
      <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', marginBottom: 32 }}>
        Die Meldung wurde erfolgreich übermittelt und wird bearbeitet.
      </p>
      <button className="btn btn-primary" onClick={() => navigate('/schaden')} style={{ minWidth: 200, justifyContent: 'center' }}>
        Zur Übersicht
      </button>
      <button className="btn btn-secondary" onClick={() => { setSubmitted(false); setForm({ title:'', description:'', damage_date: new Date().toISOString().split('T')[0], location:'', damage_type:'sonstiges' }); setImages([]); }}
        style={{ minWidth: 200, justifyContent: 'center', marginTop: 10 }}>
        Weiteren Schaden melden
      </button>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', paddingTop: 'env(safe-area-inset-top)' }}>
      {/* Header */}
      <div style={{ background: '#e63946', padding: '20px 20px 24px', paddingTop: 'calc(20px + env(safe-area-inset-top))' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
          <div style={{ width: 40, height: 40, background: 'rgba(255,255,255,.2)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <AlertTriangle size={22} color="#fff" />
          </div>
          <div>
            <div style={{ color: 'rgba(255,255,255,.8)', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Schadensmeldung</div>
            <div style={{ color: '#fff', fontWeight: 800, fontSize: '1.1rem' }}>FuhrparkGerhards</div>
          </div>
        </div>
        {vehicle && (
          <div style={{ background: 'rgba(255,255,255,.15)', borderRadius: 12, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
            <Car size={20} color="#fff" />
            <div>
              <div style={{ color: '#fff', fontWeight: 700, fontSize: '1rem' }}>{vehicle.brand} {vehicle.model}</div>
              <div style={{ color: 'rgba(255,255,255,.8)', fontSize: '0.82rem', fontFamily: 'DM Mono, monospace' }}>{vehicle.license_plate}</div>
            </div>
          </div>
        )}
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} style={{ padding: '20px 16px', paddingBottom: 'calc(20px + env(safe-area-inset-bottom))' }}>
        <div className="form-group">
          <label className="form-label">Kurzbezeichnung *</label>
          <input className="form-control" value={form.title} onChange={e => f('title', e.target.value)} required placeholder="z.B. Delle vorne links, Glasbruch..." />
        </div>
        <div className="form-group">
          <label className="form-label">Beschreibung *</label>
          <textarea className="form-control" value={form.description} onChange={e => f('description', e.target.value)} required rows={4} placeholder="Bitte den Schaden so genau wie möglich beschreiben..." />
        </div>
        <div className="form-row cols-2">
          <div className="form-group">
            <label className="form-label">Datum *</label>
            <input type="date" className="form-control" value={form.damage_date} onChange={e => f('damage_date', e.target.value)} required />
          </div>
          <div className="form-group">
            <label className="form-label">Schadensart</label>
            <select className="form-control" value={form.damage_type} onChange={e => f('damage_type', e.target.value)}>
              {DAMAGE_TYPES.map(t => <option key={t}>{t}</option>)}
            </select>
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Ort des Schadens</label>
          <input className="form-control" value={form.location} onChange={e => f('location', e.target.value)} placeholder="Straße, Stadt oder Beschreibung..." />
        </div>

        {/* Image Upload */}
        <div className="form-group">
          <label className="form-label">Fotos vom Schaden</label>
          <div style={{ border: '2px dashed var(--border)', borderRadius: 12, padding: 20, textAlign: 'center', cursor: 'pointer', background: images.length ? 'var(--green-soft)' : 'var(--bg)' }}
            onClick={() => document.getElementById('qr-imgs').click()}>
            {images.length > 0 ? (
              <div style={{ color: 'var(--green)' }}>
                <CheckCircle size={24} style={{ marginBottom: 6 }} />
                <div style={{ fontWeight: 600 }}>{images.length} Foto(s) ausgewählt</div>
                <div style={{ fontSize: '0.78rem', marginTop: 4 }}>{images.map(i => i.name).join(', ')}</div>
              </div>
            ) : (
              <div style={{ color: 'var(--text-muted)' }}>
                <Upload size={28} style={{ marginBottom: 8 }} />
                <div style={{ fontWeight: 600, marginBottom: 4 }}>Fotos aufnehmen oder hochladen</div>
                <div style={{ fontSize: '0.78rem' }}>Mehrere Bilder möglich</div>
              </div>
            )}
            <input id="qr-imgs" type="file" accept="image/*" multiple capture="environment" style={{ display: 'none' }} onChange={e => setImages(Array.from(e.target.files))} />
          </div>
          {/* Preview */}
          {images.length > 0 && (
            <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
              {images.map((img, i) => (
                <img key={i} src={URL.createObjectURL(img)} alt="" style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 8, border: '2px solid var(--border)' }} />
              ))}
            </div>
          )}
        </div>

        <button type="submit" className="btn btn-primary" disabled={saving}
          style={{ width: '100%', justifyContent: 'center', padding: '14px', fontSize: '1rem', marginTop: 8 }}>
          {saving ? <span className="spinner" style={{ width: 18, height: 18, borderWidth: 2, borderTopColor: '#fff' }} /> : <AlertTriangle size={18} />}
          Schadensmeldung abschicken
        </button>
      </form>
    </div>
  );
}
